import {
  useQuery as useTanstackQuery,
  type QueryFunctionContext,
  type QueryKey,
  type UseQueryResult,
} from '@tanstack/react-query'

export interface UseQueryOptions<T = any> {
  enabled?: boolean
  refetchOnWindowFocus?: boolean | 'always'
  refetchOnReconnect?: boolean | 'always'
  [key: string]: unknown
}

/**
 * Thin wrapper around TanStack Query v5 `useQuery` that:
 * - Filters `undefined` values from queryKey arrays (legacy convenience)
 * - Sets project-wide defaults: `retry: false`, `refetchOnWindowFocus: false`,
 *   `refetchOnReconnect: 'always'`
 * - Allows arbitrary `props` pass-through to native useQuery (e.g. `select`,
 *   `staleTime`, `refetchOnMount`)
 *
 * The previous implementation supported `onSuccess`/`onError` callbacks that
 * TanStack Query v5 removed. Those have been migrated away from the codebase
 * (entity detail forms use `data + useEffect`, errors are handled by the
 * global `QueryCache.onError` in `src/main.tsx`). This wrapper now exists
 * primarily for the queryKey filtering and default options.
 */
export function useQuery<T = any>(
  queryKey: QueryKey | undefined,
  // TQ invokes queryFn with its QueryFunctionContext — typed here so closures
  // can consume `ctx.signal` for abort forwarding (zero-arg closures still fit).
  queryFn: (ctx: QueryFunctionContext) => any,
  { enabled = true, ...props }: UseQueryOptions<T> = {},
): UseQueryResult<T, unknown> {
  const keys: QueryKey = Array.isArray(queryKey)
    ? queryKey.filter((key) => key !== undefined)
    : queryKey !== undefined
      ? [queryKey]
      : [null]

  return useTanstackQuery<T, unknown>({
    queryKey: keys,
    queryFn: queryFn as (ctx: QueryFunctionContext) => Promise<T>,
    enabled,
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: props?.refetchOnWindowFocus ?? false,
    refetchOnReconnect: 'always',
    ...props,
  }) as UseQueryResult<T, unknown>
}
